import { Link } from 'react-router'
import {
  ArrowRightIcon,
  ListChecksIcon,
  Mtds,
  toLocaleDateString
} from 'prove-common'
import type { WorkspaceTaskSummary } from '../../utils/tasksTableUtils'

type WorkspaceTasksCardProps = {
  summary: WorkspaceTaskSummary
  to: string
}

/**
 * Describes the upcoming deadline only. Overdue work is communicated by the
 * danger tag instead, so the two never repeat the same fact.
 */
const getDeadlineText = (summary: WorkspaceTaskSummary): string =>
  summary.nextDuedate == null
    ? 'Ingen kommende frister'
    : `Neste frist ${toLocaleDateString(summary.nextDuedate)}`

/**
 * Summarises the tasks in a tilsynsdivisjon and links to the full task list,
 * so the workspace page answers "is anything overdue?" without a navigation.
 *
 * Kept deliberately low-key: a single compact row on the default card surface,
 * so it supports the plan list rather than competing with the page header.
 * Colour is reserved for the overdue tag, which is the only urgent state.
 *
 * Renders nothing when the tilsynsdivisjon has no tasks at all, to avoid a
 * dead-end card.
 */
export const WorkspaceTasksCard = ({
  summary,
  to
}: WorkspaceTasksCardProps) => {
  if (summary.total === 0) return null

  const hasOverdue = summary.overdue > 0
  const linkId = 'workspace-tasks-link'

  return (
    <Mtds.Card
      data-pad='3-4'
      data-radius='md'
      className='hover:bg-surface-hover'
      data-clickdelegatefor={linkId}
    >
      <Link id={linkId} to={to} hidden />
      <Mtds.Flex
        data-align='center'
        data-justify='space-between'
        data-gap='3'
        data-wrap
      >
        <Mtds.Flex data-align='center' data-gap='3' data-wrap>
          <Mtds.Flex data-align='center' data-gap='2' data-nowrap>
            <ListChecksIcon aria-hidden />
            <strong>Oppgaver</strong>
          </Mtds.Flex>
          <Mtds.Muted>
            {summary.incomplete} av {summary.total}{' '}
            {summary.total === 1 ? 'oppgave' : 'oppgaver'} gjenstår ·{' '}
            {getDeadlineText(summary)}
          </Mtds.Muted>
          {hasOverdue && (
            <Mtds.Tag data-color='danger'>
              {summary.overdue}{' '}
              {summary.overdue === 1 ? 'forfalt frist' : 'forfalte frister'}
            </Mtds.Tag>
          )}
        </Mtds.Flex>
        <ArrowRightIcon aria-hidden />
      </Mtds.Flex>
    </Mtds.Card>
  )
}
